import {
  Anchor,
  Badge,
  Box,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { IconAlertTriangle, IconChevronRight } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { EmptyState } from "../components/ui";
import type { WorkItem } from "../lib/api";
import { formatINR } from "../lib/money";
import { Page } from "../shell/Page";
import { moduleAccent } from "../shell/modules";
import { useMyWork } from "../shell/MyWorkContext";

/**
 * Home: what this person has to do now, and only then where everything is.
 *
 * WHAT CHANGED AND WHY
 *
 * This was a flat wall of module tiles - seven identical cards, each naming a
 * module. It answered "where is Service?", which the nav already answers from
 * every screen, and never answered the question somebody actually opens the
 * app with, which is "what is waiting for me?". A Store Manager had to visit
 * four screens to find out whether anything needed doing.
 *
 * So the numbers come first and the tiles are demoted beneath them. The
 * ordering is the point: a screen's first section is what it is FOR.
 *
 * EVERY ROW IS A SHORTCUT, NOT A SOURCE
 *
 * A work row's tap target is the ALREADY-FILTERED list - "/service/queue
 * ?stage=COLLECTED", not "/service" - because a number that drops you on a
 * landing screen has made you do the filtering twice. Nothing on this screen
 * is reachable from nowhere else, which is why a failed count request is a
 * missing shortcut rather than an error page.
 *
 * WHERE THE COUNTS COME FROM
 *
 * One request, counts only, scoped by the server to the modules and rows this
 * person may open - see src/services/worklist.service.ts. The same snapshot
 * feeds the nav badges, so a badge and a row can never disagree.
 *
 * WHAT IS NOT HERE
 *
 * The brief's desktop header carries a 320px global search with a "/"
 * shortcut. There is no cross-module search endpoint - searching "serial no.,
 * customer, job, SKU" in one box is four queries this API does not offer - so
 * a box that silently searched nothing is left out rather than mocked up.
 */
export function Home() {
  const { user } = useAuth();
  const work = useMyWork();

  return (
    <Page
      title="Supply X"
      /*
       * A GREETING, NOT A ROLE LIST.
       *
       * This read "<name> · <every role they hold>", which for somebody who
       * is both a Delivery Agent and an Outdoor Service Technician spelled
       * both out under the app name on every visit. Nobody opens their home
       * screen to be told what their job is - and the people with the longest
       * list are the field staff on the narrowest phones.
       *
       * The role has not gone anywhere: Your profile shows it, which is where
       * somebody goes to check which account they are signed in to.
       */
      subtitle={user ? `Hello, ${user.name}` : undefined}
      width="wide"
    >
      <YourWorkNow />

      {work.missingDeliveries > 0 && (
        <MissingDeliveries count={work.missingDeliveries} />
      )}

    </Page>
  );
}

/**
 * The first thing on the screen.
 *
 * One card on a phone - a header with the total and a divided list - and a
 * grid of cards from md up, where a single column of five rows would leave
 * most of a 1440px screen empty. Both are drawn from one array, so the copy
 * cannot drift between the two.
 */
function YourWorkNow() {
  const { items, total, status, reload } = useMyWork();

  if (status === "error") {
    // Keeps the card and its heading.
    //
    // Home used to carry a module grid underneath, so a failed count request
    // still left a page with something on it. Now that the work IS the page,
    // a bare error line leaves Home looking broken rather than looking like a
    // screen whose shortcuts did not load - and the difference matters,
    // because nothing here is the only way to reach anything.
    return (
      <Card withBorder padding={0}>
        <WorkHeader total={null} />
        <Group justify="space-between" wrap="nowrap" gap="sm" p="md">
          <Text fz="sm" c="gray.7">
            Could not load what is waiting for you. Nothing is lost — every
            list is still in the menu on the left.
          </Text>
          <Anchor component="button" type="button" fz="sm" fw={600} onClick={reload}>
            Try again
          </Anchor>
        </Group>
      </Card>
    );
  }

  if (status === "loading") {
    return (
      <Card withBorder padding={0}>
        <WorkHeader total={null} />
        <Box p="md">
          <Text fz="sm" c="gray.6">
            Checking what is waiting…
          </Text>
        </Box>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card withBorder padding={0}>
        <WorkHeader total={0} />
        <EmptyState
          title="Nothing is waiting on you"
          description="When a battery, an order or a delivery needs your step, it appears here."
        />
      </Card>
    );
  }

  return (
    <>
      {/* Phone and tablet: one card, rows divided by a hairline. */}
      <Box hiddenFrom="md">
        <Card withBorder padding={0}>
          <WorkHeader total={total} />
          <Stack gap={0}>
            {items.map((item) => (
              <WorkRow key={item.key} item={item} />
            ))}
          </Stack>
        </Card>
      </Box>

      {/* Desktop: the same items as a 4-up grid. */}
      <Box visibleFrom="md">
        <Stack gap="xs">
          <Group gap="sm" align="center">
            <SectionLabel>Your work now</SectionLabel>
            <TotalPill total={total} />
          </Group>
          <SimpleGrid cols={{ md: 2, lg: 4 }} spacing={10}>
            {items.map((item) => (
              <WorkTile key={item.key} item={item} />
            ))}
          </SimpleGrid>
        </Stack>
      </Box>
    </>
  );
}

function WorkHeader({ total }: { total: number | null }) {
  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      px={14}
      py={10}
      style={{ borderBottom: "1px solid var(--mantine-color-gray-2)" }}
    >
      <SectionLabel>Your work now</SectionLabel>
      {total !== null && <TotalPill total={total} />}
    </Group>
  );
}

function TotalPill({ total }: { total: number }) {
  return (
    <Badge
      radius="sm"
      variant="filled"
      color="indigo.7"
      styles={{ root: { fontWeight: 700, fontVariantNumeric: "tabular-nums" } }}
    >
      {total}
    </Badge>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <Text fz={12} fw={700} c="gray.7" tt="uppercase" style={{ letterSpacing: ".04em" }}>
      {children}
    </Text>
  );
}

/** The money line on a delivery round: what is still owed, across how many stops. */
function toCollectLine(item: WorkItem): string | null {
  if (!item.toCollect) return null;
  const stops = item.count === 1 ? "1 stop" : `${item.count} stops`;
  return `${formatINR(item.toCollect)} to collect across ${stops}`;
}

/**
 * One work row on a phone.
 *
 * A real button rather than a Card with an onClick, so it is in the tab order
 * and answers the Enter key without any of that being re-implemented here.
 */
function WorkRow({ item }: { item: WorkItem }) {
  const navigate = useNavigate();
  const accent = moduleAccent(item.module);
  const money = toCollectLine(item);

  return (
    <UnstyledButton
      onClick={() => navigate(item.href)}
      px={14}
      py={12}
      style={{
        borderLeft: `3px solid ${accent.fg}`,
        borderBottom: "1px solid var(--mantine-color-gray-1)",
        minHeight: 44,
      }}
    >
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <Text
          fz={26}
          fw={600}
          lh={1.1}
          style={{ minWidth: 34, fontVariantNumeric: "tabular-nums" }}
          c={item.tone === "alert" ? "red.8" : undefined}
        >
          {item.count}
        </Text>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text fz={15} fw={600} lh={1.3}>
            {item.title}
          </Text>
          <Text fz={13} c="gray.6" lh={1.35}>
            {item.subtitle}
          </Text>
          {money && (
            <Text fz={13} fw={600} c="gray.8" lh={1.35}>
              {money}
            </Text>
          )}
        </Box>
        <IconChevronRight size={18} stroke={1.8} color="#adb5bd" />
      </Group>
    </UnstyledButton>
  );
}

/** The same item as a desktop card. */
function WorkTile({ item }: { item: WorkItem }) {
  const navigate = useNavigate();
  const accent = moduleAccent(item.module);
  const money = toCollectLine(item);

  return (
    <UnstyledButton onClick={() => navigate(item.href)} style={{ height: "100%" }}>
      <Card
        withBorder
        padding="md"
        h="100%"
        style={{ borderLeft: `3px solid ${accent.fg}` }}
      >
        <Stack gap={4}>
          <Text
            fz={32}
            fw={600}
            lh={1.05}
            style={{ fontVariantNumeric: "tabular-nums" }}
            c={item.tone === "alert" ? "red.8" : undefined}
          >
            {item.count}
          </Text>
          <Text fz={15} fw={600} lh={1.3}>
            {item.title}
          </Text>
          <Text fz={13} c="gray.6" lh={1.35}>
            {item.subtitle}
          </Text>
          {money && (
            <Text fz={13} fw={600} c="gray.8" lh={1.35}>
              {money}
            </Text>
          )}
        </Stack>
      </Card>
    </UnstyledButton>
  );
}

/**
 * The queue disagreeing with Sales and Service.
 *
 * Its own warning rather than another numbered row, because it is not a step
 * anybody was asked to take - it is records that should already be in the
 * Delivery queue and are not. The link goes to /delivery, where the reconcile
 * action lives.
 */
function MissingDeliveries({ count }: { count: number }) {
  return (
    <Card
      withBorder
      padding={14}
      style={{ background: "#fff9db", borderColor: "#ffe066" }}
    >
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <IconAlertTriangle size={18} stroke={1.8} color="#a1710a" />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text fz={14} fw={600}>
            {count === 1
              ? "1 delivery is missing from the queue"
              : `${count} deliveries are missing from the queue`}
          </Text>
          <Text fz={13} c="gray.7">
            Billed in Sales or finished in Service but never handed over.{" "}
            <Anchor component={Link} to="/delivery" fz={13} fw={600} c="indigo.7">
              Hand them over now
            </Anchor>
          </Text>
        </Box>
      </Group>
    </Card>
  );
}


